import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter
} from 'firebase/firestore'
import { db } from 'firebaseClient'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Game } from 'types'

interface UseGetGamesProps {
  round: number
  enabled?: boolean
}

const useGetGames = ({ enabled = true, round }: UseGetGamesProps) => {
  const [games, setGames] = useState<Game[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const lastDocRef = useRef<QueryDocumentSnapshot | undefined>()
  const [hasMore, setHasMore] = useState<boolean>(false)
  const pageSize = 20

  const fetchGames = useCallback(
    async (lastDoc?: QueryDocumentSnapshot) => {
      if (!enabled) return

      setIsLoading(true)

      const queryConstraints: QueryConstraint[] = [
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      ]

      if (lastDoc) {
        queryConstraints.push(startAfter(lastDoc))
      }

      const q = query(
        collection(db, 'games', `${round}`, 'games'),
        ...queryConstraints
      )

      getDocs(q)
        .then((querySnapshot) => {
          const data: Game[] = []
          querySnapshot.forEach((doc) => {
            data.push(doc.data() as Game)
          })
          if (lastDoc) {
            setGames((prev) => [...prev, ...data])
          } else {
            setGames(data)
          }
          lastDocRef.current = querySnapshot.docs[querySnapshot.docs.length - 1]
          setHasMore(querySnapshot.docs.length === pageSize)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [round, enabled]
  )

  useEffect(() => {
    fetchGames()
  }, [fetchGames])

  return {
    fetchNextPage: useCallback(() => {
      fetchGames(lastDocRef.current)
    }, [fetchGames]),
    games,
    hasMore,
    isLoading
  }
}

export default useGetGames
